import { ArrowUpRight, Quote, Headphones, FileText, Mail, Download } from "lucide-react";
import { CornerMarks, StatusLED } from "@/components/infra";

const ARTICLE_COVER =
  "https://customer-assets.emergentagent.com/job_book-minimal-1/artifacts/3sne3ho4_NEDAS-Live-E63_Luke-Adams_Blog.png";
const PODCAST_COVER =
  "https://customer-assets.emergentagent.com/job_book-minimal-1/artifacts/c22sh75q_1765297279732.jpeg";

const ARTICLE_URL =
  "https://datacenterpost.com/building-the-next-generation-of-data-center-leaders-a-conversation-with-luke-adams/";
const PODCAST_URL = "https://www.nedas.com/e63-building-the-next-generation-of-data-center-leaders-a-conversation-with-luke-adams/";

const EXCERPTS = [
  {
    code: "01",
    title: "Creating opportunity in the foundation of AI",
    body: "DPGlobal Assets specializes in global digital infrastructure development, from ideation through operation. Adams shares what drew him in: the realization that data centers are the heart of the AI revolution and the backbone of the digital world.",
    quote:
      "Data centers are the reason that ChatGPT exists, and they're the reason that AI will continue to skyrocket.",
  },
  {
    code: "02",
    title: "Launching Data Center Youngbloods",
    body: "Most industry events were filled with seasoned professionals, making young entrants feel out of place. DCYB makes digital-infrastructure careers visible, accessible, and welcoming — bridging the workforce gap and connecting newcomers with mentorship, certification pathways, and a peer community.",
    quote:
      "We're building the community that I wish existed when I first started out.",
  },
  {
    code: "03",
    title: "Mentorship — and debunking the myths",
    body: "Young professionals often get discouraged by strict experience requirements. Adams urges them to be curious, proactive, and fearless in asking questions. Skills and knowledge can be gained on the job with the right attitude — DCYB is cultivating in-person events, virtual meetings, and access to supportive mentors.",
    quote: null,
  },
  {
    code: "04",
    title: "Driving change, one conversation at a time",
    body: "As the network expands, the message centers on paying it forward. DCYB welcomes both seasoned professionals and emerging talent, with a booking portal for mentorship and a growing community through LinkedIn and direct outreach.",
    quote:
      "Everything is learnable. Be proactive, get involved, and don't be afraid to reach out, no matter your background.",
  },
];

export default function Press() {
  return (
    <section
      id="press"
      data-testid="press-section"
      className="border-b hairline bg-white relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        {/* Header */}
        <div className="grid grid-cols-12 gap-y-12 gap-x-6 mb-12">
          <div className="col-span-12 lg:col-span-5">
            <div data-testid="press-section-label" className="font-mono-label text-[11px] text-[#0F1117] mb-6 flex items-center gap-3">
              <StatusLED />
              / In conversation
            </div>
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-extrabold text-[#0F1117] text-balance">
              Featured on <span className="text-chrome">NEDAS Live.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <p className="text-base sm:text-lg text-[#3D4049] leading-relaxed max-w-xl">
              Episode 63 of NEDAS Live!, hosted by{" "}
              <strong className="text-[#0F1117] font-semibold">Ilissa Miller</strong>{" "}
              — CEO of iMiller Public Relations — explores how a young leader is
              paving the way for new talent and greater inclusivity in the digital
              infrastructure sector. Available as a written feature and a podcast.
            </p>
          </div>
        </div>

        {/* Two featured formats: Article + Podcast */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          {/* Article */}
          <article
            data-testid="press-article-card"
            className="chrome-surface relative flex flex-col"
          >
            <CornerMarks color="#A9ADB4" />
            <div className="p-2 bg-white">
              <img
                src={ARTICLE_COVER}
                alt="NEDAS Live Episode 63 — article cover"
                className="w-full h-auto object-contain"
                draggable="false"
              />
            </div>
            <div className="p-8 md:p-9 border-t hairline flex flex-col flex-1">
              <div className="flex items-center justify-between mb-5">
                <span className="font-mono-label text-[10px] text-[#71747B] flex items-center gap-1.5">
                  <FileText size={11} /> / WRITTEN FEATURE
                </span>
                <span className="font-mono-label text-[10px] text-[#0F1117]">
                  Data Center Post
                </span>
              </div>
              <h3 className="font-display text-xl md:text-2xl font-extrabold text-[#0F1117] tracking-tight leading-tight mb-3">
                Building the Next Generation of{" "}
                <span className="text-chrome">Data Center Leaders.</span>
              </h3>
              <p className="text-[14px] text-[#52555B] leading-relaxed mb-6">
                A long-form conversation on how Adams transitioned from college
                graduate to industry analyst, why he co-founded DCYB, and the
                mentorship gap he&apos;s working to close.
              </p>
              <a
                href={ARTICLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="press-read-article-btn"
                className="chrome-btn-dark inline-flex w-fit items-center gap-2 font-mono-label text-[12px] px-6 py-4 mt-auto"
              >
                Read the article
                <ArrowUpRight size={16} />
              </a>
            </div>
          </article>

          {/* Podcast */}
          <article
            data-testid="press-podcast-card"
            className="chrome-surface relative flex flex-col"
          >
            <CornerMarks color="#A9ADB4" />
            <div className="p-2 bg-white">
              <img
                src={PODCAST_COVER}
                alt="NEDAS Live Episode 63 — podcast cover"
                className="w-full h-auto object-contain"
                draggable="false"
              />
            </div>
            <div className="p-8 md:p-9 border-t hairline flex flex-col flex-1">
              <div className="flex items-center justify-between mb-5">
                <span className="font-mono-label text-[10px] text-[#71747B] flex items-center gap-1.5">
                  <Headphones size={11} /> / PODCAST EPISODE
                </span>
                <span className="font-mono-label text-[10px] text-[#0F1117] flex items-center gap-1.5">
                  <StatusLED color="#10B981" />
                  Listen
                </span>
              </div>
              <h3 className="font-display text-xl md:text-2xl font-extrabold text-[#0F1117] tracking-tight leading-tight mb-3">
                What does it really take to build the{" "}
                <span className="text-chrome">next generation?</span>
              </h3>
              <p className="text-[14px] text-[#52555B] leading-relaxed mb-6">
                {"In this episode, we explore what it really takes to build the next generation of talent in the digital infrastructure industry. Luke shares his journey from discovering data centers in college to launching a fast-growing community designed to make the industry more visible, accessible, and welcoming to young professionals — the gaps in education-to-career pathways, the power of mentorship, the misconceptions young people have about entering the field, and why curiosity, resilience, and real human connection are essential for the future of digital infrastructure."}
              </p>
              <a
                href={PODCAST_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="press-listen-podcast-btn"
                className="chrome-btn-dark inline-flex w-fit items-center gap-2 font-mono-label text-[12px] px-6 py-4 mt-auto"
              >
                <Headphones size={14} />
                Listen on NEDAS
                <ArrowUpRight size={16} />
              </a>
            </div>
          </article>
        </div>

        {/* Excerpts grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 border-l border-t hairline mb-12">
          {EXCERPTS.map(({ code, title, body, quote }) => (
            <article
              key={code}
              data-testid={`press-excerpt-${code}`}
              className="border-r border-b hairline p-7 md:p-9 bg-gradient-to-b from-white to-[#F4F5F7] hover:from-white hover:to-[#E8EAED] transition-colors flex flex-col"
            >
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono-label text-[10px] text-[#71747B]">
                  / EXCERPT · {code}
                </span>
                {quote && (
                  <Quote
                    size={14}
                    className="text-[#0F1117] -scale-x-100"
                    aria-hidden
                  />
                )}
              </div>
              <h4 className="font-display text-xl md:text-2xl font-bold text-[#0F1117] tracking-tight mb-3 leading-tight">
                {title}
              </h4>
              <p className="text-[14px] text-[#52555B] leading-relaxed mb-5">
                {body}
              </p>
              {quote && (
                <blockquote className="mt-auto pt-5 border-t hairline">
                  <p className="font-display text-[15px] md:text-base text-[#0F1117] leading-snug italic">
                    &ldquo;{quote}&rdquo;
                  </p>
                  <footer className="mt-2 font-mono-label text-[10px] text-[#71747B]">
                    — Luke Adams
                  </footer>
                </blockquote>
              )}
            </article>
          ))}
        </div>

        {/* Closing CTA band */}
        <div className="chrome-surface relative p-6 md:p-8 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="font-mono-label text-[10px] text-[#71747B] mb-2">
              / Want the whole conversation?
            </div>
            <h4 className="font-display text-xl md:text-2xl font-bold text-[#0F1117] tracking-tight">
              Read the article, then hit play on the podcast.
            </h4>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={ARTICLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="press-cta-article-btn"
              className="chrome-btn inline-flex items-center gap-2 font-mono-label text-[12px] px-6 py-4"
            >
              <FileText size={14} />
              Read article
              <ArrowUpRight size={16} />
            </a>
            <a
              href={PODCAST_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="press-cta-podcast-btn"
              className="chrome-btn-dark inline-flex items-center gap-2 font-mono-label text-[12px] px-6 py-4"
            >
              <Headphones size={14} />
              Listen
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
        {/* Press & Media — boilerplate, inquiries, press kit */}
        <div
          data-testid="press-media-block"
          className="mt-12 border-t hairline pt-12 grid grid-cols-12 gap-y-8 gap-x-6"
        >
          <div className="col-span-12 lg:col-span-5">
            <div className="font-mono-label text-[11px] text-[#0F1117] mb-4 flex items-center gap-3">
              <StatusLED />
              / Press &amp; Media
            </div>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0F1117] leading-[1.05]">
              For journalists,<br />
              <span className="text-chrome">podcasts, and editors.</span>
            </h3>
          </div>

          <div className="col-span-12 lg:col-span-7 lg:pl-8 space-y-6">
            <p className="text-[15px] md:text-base text-[#3D4049] leading-relaxed">
              Data Center Youngbloods (DCYB) is the community closing the workforce
              gap behind the AI-era digital infrastructure buildout. Founded in 2025
              by Luke Adams and JohnPaul Adams, DCYB brings together the next
              generation of operators, engineers, and builders, and connects them
              directly to the hyperscalers, colocation operators, and partners
              shaping the industry. Based in the United States, active worldwide.
            </p>

            <div className="flex flex-wrap gap-3">
              <a
                href="mailto:Luke@DCYB.ai?subject=Press%20inquiry%20%E2%80%94%20DCYB"
                data-testid="press-inquiries-email-btn"
                className="chrome-btn-dark inline-flex items-center gap-2 font-mono-label text-[12px] tracking-[0.14em] px-6 py-4"
              >
                <Mail size={14} />
                Press inquiries
                <ArrowUpRight size={16} />
              </a>
              <a
                href="mailto:Luke@DCYB.ai?subject=Press%20kit%20request%20%E2%80%94%20DCYB&body=Hi%20Luke%2C%0A%0APlease%20send%20over%20the%20DCYB%20press%20kit.%20Thanks."
                data-testid="press-kit-btn"
                className="chrome-btn inline-flex items-center gap-2 font-mono-label text-[12px] tracking-[0.14em] px-6 py-4"
              >
                <Download size={14} />
                Request press kit
                <ArrowUpRight size={16} />
              </a>
            </div>

            <div className="font-mono-label text-[10px] tracking-[0.14em] text-[#71747B] leading-relaxed pt-2 border-t hairline">
              MEDIA CONTACT · Luke Adams, Co-Founder · Luke@DCYB.ai
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
